import type React from "react";
import { useCallback, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSWRConfig } from "swr";
import { IconDownload } from "@tabler/icons-react";
import { Heading, Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { Chip } from "../../../../components/common/chip";
import { CurrencyAmount } from "../../../../components/common/currency_amount";
import { Image } from "../../../../components/common/image";
import { ShimmerLoader, Shimmer } from "../../../../components/common/shimmer";
import { SimpleTable, type SimpleTableColumn } from "../../../../components/common/simple-table";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { showErrorToast } from "../../../../utils/toasts";
import type { SaleOrderLineDto } from "../../../../api";
import { useQuotationDetails } from "../../hooks/useQuotationDetails";
import { useRenewQuotation } from "../../../sales/hooks/useRenewQuotation";
import { deriveQuotationStatus, getExpiryInfo } from "../../utils/quotation-status";
import { getQuotationCheckoutUrl } from "../../utils/get-quotation-checkout-url";
import { productVariantsService, salesService } from "../../../api_client";
import { getProductDetailsUrl } from "../../../products/utils/get-product-details-url";

const LINE_ITEM_COLUMNS: SimpleTableColumn<SaleOrderLineDto>[] = [
  {
    key: "product",
    label: "Product",
    className: "flex flex-[16] min-w-0 items-center gap-3",
    headerClassName: "flex-[16]",
    render: (item) => (
      <>
        <div className="h-12 w-12 shrink-0 overflow-hidden rounded-xl bg-bgLight">
          <Image
            image={item.image ?? null}
            alt={item.product?.name ?? item.name}
            size="w-full h-full object-cover"
          />
        </div>
        <div className="flex min-w-0 flex-col gap-0.5">
          <Text variant="bodyBold" tone="dark" className="truncate">
            {item.product?.name ?? item.name}
          </Text>
          {item.product?.id != null && (
            <Text variant="bodyXs" tone="muted">ID #{item.product.id}</Text>
          )}
          {item.name && item.name !== item.product?.name && (
            <Text variant="bodySm" tone="body" className="truncate">
              {item.name}
            </Text>
          )}
        </div>
      </>
    ),
  },
  {
    key: "qty",
    label: "QTY",
    className: "flex-[5]",
    render: (item) => <Text variant="bodyBold" tone="dark" inline>×{item.quantity}</Text>,
  },
  {
    key: "unitPrice",
    label: "Unit Price",
    className: "flex-[6]",
    render: (item) => <CurrencyAmount amount={item.unitPrice} variant="bodySm" tone="dark" />,
  },
  {
    key: "subtotal",
    label: "Subtotal",
    className: "flex-[6]",
    headerClassName: "flex-[6] text-right",
    render: (item) => (
      <div className="text-right">
        <CurrencyAmount amount={item.subtotal} variant="bodyBold" tone="dark" />
      </div>
    ),
  },
];

export function QuotationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { mutate } = useSWRConfig();
  const { renewQuotation } = useRenewQuotation();

  const numericId = id != null ? Number(id) : NaN;
  const validId = Number.isFinite(numericId);
  const { data, error, isLoading } = useQuotationDetails(validId ? numericId : null);

  const [renewing, setRenewing] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const status = data ? deriveQuotationStatus(data.state, data.validityDate) : null;
  const expiry = data ? getExpiryInfo(data.validityDate, data.dateOrder) : null;
  const currency = data?.currency?.name ?? "QAR";
  const quotationName = data?.name ?? "Quotation";

  const handleBuy = useCallback(() => {
    if (!data) return;
    navigate(getQuotationCheckoutUrl(data.id));
  }, [data, navigate]);

  const handleLineClick = useCallback(
    async (line: SaleOrderLineDto) => {
      if (!data) return;
      const variantId = line.product?.id;
      if (variantId == null) return;

      try {
        const response = await productVariantsService.getProductVariantById(variantId);
        const variant = response.data;
        const templateId = variant.productTemplateId ?? variant.id;
        navigate(
          getProductDetailsUrl(
            templateId,
            variant.id,
            AppRoutes.PRODUCT_DETAILS,
          ),
          {
            state: {
              breadcrumbTrail: [
                { label: "Quotations", path: AppRoutes.QUOTATIONS },
                {
                  label: quotationName,
                  path: AppRoutes.QUOTATION_DETAILS.replace(":id", String(data.id)),
                },
              ],
            },
          },
        );
      } catch {
        showErrorToast("Could not open the product details for this item.");
      }
    },
    [data, navigate, quotationName],
  );

  // Through the backend rather than Odoo's portal link: it serves a copy
  // without prices to buyers whose prices are hidden.
  const handleDownload = useCallback(async () => {
    if (!data) return;
    setDownloading(true);
    try {
      const res = await salesService.getSaleOrderPdf(data.id, { responseType: "blob" });
      const url = URL.createObjectURL(res.data as unknown as Blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Quotation - ${data.name}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      showErrorToast("Could not download the quotation. Please try again.");
    } finally {
      setDownloading(false);
    }
  }, [data]);

  const handleRenew = useCallback(async () => {
    if (!data) return;
    setRenewing(true);
    const ok = await renewQuotation(data.id);
    setRenewing(false);
    if (ok) {
      await mutate(`quotation-details-${data.id}`);
      await mutate((key) => typeof key === "string" && key.startsWith("quotations-"));
    }
  }, [data, renewQuotation, mutate]);

  if (!validId) {
    return <NotFound />;
  }

  if (isLoading) {
    return (
      <div className="flex w-full flex-col gap-6 px-8 py-6">
        <ShimmerLoader message="Loading quotation details…">
          <div className="flex flex-col gap-4">
            <Shimmer className="h-6 w-1/3" />
            <Shimmer className="h-32 w-full" />
            <Shimmer className="h-64 w-full" />
          </div>
        </ShimmerLoader>
      </div>
    );
  }

  if (error || !data) {
    return <NotFound />;
  }

  const itemCount = data.orderLines?.length ?? 0;
  const isPaid = status === "paid";
  const isExpired = status === "expired";
  const isCancelled = status === "cancelled";

  return (
    <div className="flex w-full flex-col gap-6 px-8 py-6 max-md:gap-4 max-md:px-4">
      <div className="flex items-center justify-between max-md:flex-wrap max-md:gap-3">
        <div className="flex items-center gap-1.5">
          <Link to={AppRoutes.QUOTATIONS} className="transition-colors hover:text-textDark">
            <Text variant="bodySm" tone="muted" inline>Quotations</Text>
          </Link>
          <Text variant="bodySm" tone="muted" inline>/</Text>
          <Text variant="bodySm" tone="dark" inline>{data.name}</Text>
        </div>
        <div className="flex items-center gap-2">
          {!isPaid && !isCancelled && !isExpired && (
            <Button variant="primary" size="sm" className="px-6" onClick={handleBuy}>
              Buy Now
            </Button>
          )}
          {isExpired && (
            <Button
              variant="primary"
              size="sm"
              className="px-6"
              onClick={handleRenew}
              disabled={renewing}
            >
              {renewing ? "Renewing…" : "Renew"}
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<IconDownload size={14} className="text-textDark" />}
            onClick={handleDownload}
            disabled={downloading}
          >
            {downloading ? "Downloading…" : "Download"}
          </Button>
        </div>
      </div>

      <div className="flex gap-4 max-md:grid max-md:grid-cols-2 max-md:gap-3">
        <div className="max-md:col-span-2 max-md:w-full flex w-[40%] shrink-0 flex-col gap-2 rounded-xl border border-borderDefault bg-bgWhite p-5">
          <div className="flex items-center gap-2">
            <Heading variant="h3" tone="dark">{data.name}</Heading>
            <StatusChipForState status={status ?? "pending"} />
          </div>
          <Text variant="bodySm" tone="muted">
            {itemCount} item{itemCount === 1 ? "" : "s"}
          </Text>
          <Text variant="bodyXs" tone="muted">
            Created {formatDate(data.dateOrder)}
          </Text>
        </div>

        <div className="max-md:w-full max-md:p-4 flex w-[30%] shrink-0 flex-col gap-3 rounded-xl border border-borderDefault bg-bgWhite p-5">
          <Text variant="bodyXs" tone="muted">
            {isExpired ? "Expired" : "Expires in"}
          </Text>
          <Heading variant="h3" tone="dark">
            {expiry?.label ?? (data.validityDate ? formatDate(data.validityDate) : "—")}
          </Heading>
          {expiry && !expiry.isExpired && (
            <div className="h-1 w-1/2 overflow-hidden rounded-full bg-bgMuted">
              <div
                className="h-full rounded-full bg-brandPrimary [width:var(--progress)]"
                style={{ "--progress": `${expiry.progress * 100}%` } as React.CSSProperties}
              />
            </div>
          )}
        </div>

        <div className="max-md:w-full max-md:p-4 flex w-[30%] shrink-0 flex-col gap-1.5 rounded-xl border border-borderDefault bg-bgWhite p-5">
          <CurrencyAmount amount={data.amountTotal} currency={currency} variant="h3" tone="dark" />
          <Text variant="bodyXs" tone="muted">
            Total incl. VAT · {itemCount} line item{itemCount === 1 ? "" : "s"}
          </Text>
          <Text variant="bodySm" tone="body">
            Subtotal: <CurrencyAmount amount={data.amountUntaxed} currency={currency} variant="bodySm" tone="dark" />
          </Text>
        </div>
      </div>

      <SimpleTable
        columns={LINE_ITEM_COLUMNS}
        data={data.orderLines ?? []}
        keyExtractor={(item) => String(item.id)}
        onRowClick={handleLineClick}
        footer={
          <div className="flex items-center justify-end gap-2">
            <Text variant="bodySm" tone="muted" inline>Total (Excl. VAT)</Text>
            <CurrencyAmount
              amount={data.amountUntaxed}
              currency={currency}
              variant="bodyLgBold"
              tone="dark"
            />
          </div>
        }
      />
    </div>
  );
}

function StatusChipForState({
  status,
}: {
  status: ReturnType<typeof deriveQuotationStatus>;
}) {
  if (status === "paid") return <Chip variant="approved">Paid</Chip>;
  if (status === "expired") return <Chip variant="expired">Expired</Chip>;
  if (status === "cancelled") return <Chip variant="rejected">Cancelled</Chip>;
  return <Chip variant="approved">Active</Chip>;
}

function NotFound() {
  return (
    <div className="flex w-full flex-col gap-4 px-8 py-12 text-center">
      <Heading variant="h3" tone="dark">Quotation not found</Heading>
      <Text variant="bodySm" tone="muted">
        The quotation you're looking for doesn't exist or you don't have access to it.
      </Text>
      <div className="flex justify-center">
        <Link to={AppRoutes.QUOTATIONS}>
          <Button variant="secondary" size="sm">Back to quotations</Button>
        </Link>
      </div>
    </div>
  );
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}
